import { InlineFeedback, useFeedback } from '@mts/ui/feedback';

import React, { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Input } from '@mts/ui';
import { authClient } from '../lib/authClient';
import { getMagicLinkErrorMessage } from '../utils/magicLinkErrors';
import { usePublicTenant } from '../hooks/usePublicTenant';
import { getApiBaseUrl, getTenantSlugFromHostname } from '../services/api';

function LoginPage() {
  const toast = useFeedback();
  const [searchParams] = useSearchParams();
  const { data: session, isPending } = authClient.useSession();
  const { spotlightName } = usePublicTenant();
  const [form, setForm] = useState({ email: '' });
  const [error, setError] = useState('');

  const [submitting, setSubmitting] = useState(false);

  const claimListingSlug = searchParams.get('listingSlug') || searchParams.get('listingId');
  const promotionCode = searchParams.get('promo') || sessionStorage.getItem('mtsPromotionCode') || '';
  const claimReturnPath = claimListingSlug ? `/listings/${encodeURIComponent(claimListingSlug)}/claim${promotionCode ? `?promo=${encodeURIComponent(promotionCode)}` : ''}` : '/profile';
  const registerParams = new URLSearchParams();
  if (claimListingSlug) registerParams.set('listingSlug', claimListingSlug);
  if (promotionCode) registerParams.set('promo', promotionCode);
  const queryErrorMessage = getMagicLinkErrorMessage(searchParams.get('error'));
  const activeErrorMessage = error || queryErrorMessage;

  if (session?.user?.id) {
    return <Navigate to={claimReturnPath} replace />;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');


    try {
      const tenantSlug = getTenantSlugFromHostname();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (tenantSlug) {
        headers['X-Tenant-Slug'] = tenantSlug;
      }
      const response = await fetch(`${getApiBaseUrl()}/api/auth/web-magic-link/request`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          email: form.email,
          webOrigin: window.location.origin,
          returnTo: claimReturnPath,
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || payload.error || 'Unable to send sign-in link.');
      }

      toast.success(`We sent a secure sign-in link to ${form.email}. Open your email to continue.`, { id: 'action:LoginPage:handleSubmit' });
    } catch (magicLinkError) {
      toast.error(magicLinkError.message || 'Unable to send sign-in link.', { id: 'action:LoginPage:handleSubmit' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-background px-4 py-12">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(42rem 42rem at 8% -10%, color-mix(in srgb, var(--primary) 18%, transparent), transparent 65%), radial-gradient(32rem 32rem at 92% 10%, color-mix(in srgb, var(--secondary) 16%, transparent), transparent 68%)',
        }}
      />

      <div className="relative mx-auto flex w-full max-w-md items-center justify-center">
        <Card
          padding="lg"
          shadow="lg"
          className="w-full border"
          style={{
            backgroundColor: 'var(--surface)',
            borderColor: 'var(--border)',
          }}
        >
          <div className="space-y-5">
            <div className="space-y-2 text-center">
              <h1 className="font-ui text-2xl font-semibold" style={{ color: 'var(--primary)' }}>
                Sign In
              </h1>
              <p className="text-sm" style={{ color: 'var(--page-body-color)' }}>
                Sign In To Manage Your Profile for {spotlightName || 'My Town Spotlight'}
              </p>
            </div>

            {activeErrorMessage ? (
              <InlineFeedback variant="error"
                className="rounded-lg border px-3 py-2 text-sm"
                style={{
                  borderColor: 'color-mix(in srgb, var(--danger) 40%, var(--border))',
                  color: 'var(--danger)',
                  backgroundColor: 'color-mix(in srgb, var(--danger) 10%, var(--surface))',
                }}
              >
                {activeErrorMessage}
              </InlineFeedback>
            ) : null}



            <form onSubmit={handleSubmit} className="ph-no-capture space-y-3">
              <Input
                type="email"
                name="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                required
                disabled={submitting || isPending}
                className="border-[var(--ui-input-border)] text-[var(--ui-input-text)] placeholder:text-[color:color-mix(in_srgb,var(--ui-input-text)_58%,white)] focus:border-[var(--primary)] focus:ring-[var(--primary)]"
              />
              <Button type="submit" className="w-full" disabled={submitting || isPending} loading={submitting}>
                {submitting ? 'Sending link...' : 'Email Me a Sign-In Link'}
              </Button>
            </form>

            <p className="text-sm" style={{ color: 'var(--page-body-color)' }}>
              Need an account?{' '}
              <Link
                to={`/register${registerParams.size ? `?${registerParams.toString()}` : ''}`}
                className="font-medium underline decoration-2 underline-offset-2 transition-colors"
                style={{ color: 'var(--primary)' }}
              >
                Create one
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default LoginPage;
